import React, { FC, Fragment, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ButtonType, IdEnums, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';
import { useGamePolling } from '@nhl/react-hooks/lib/useGamePolling';
import { useScroll } from '@nhl/react-hooks/lib/useScroll';

import commonMessages from '~/core/commonMessages';
import { PageName } from '~/core/enums';
import { SpecialEvent } from '~/core/tableColumns/scheduleTableColumns/enums';
import { BettingOddPartner } from '~/core/types';
import { defaultLocationData, useLocationQuery } from '~/redux/ducks/location';
import NoContent from '~/views/components/noContent/NoContent';
import OddsProvider from '~/views/components/oddsProvider/OddsProvider';

import { getGamesByDay, useClubScheduleWeekQuery, useClubSeasonScheduleQuery, useScheduleQuery } from '../../api';
import { ScheduleViewType } from '../../enums';
import { ClubScheduleButton, EventIndicatorSmall } from '../../styles';
import { DaysWithOdds, OddDetails, Schedule as ScheduleType, ScheduleGame, ViewProps } from '../../types';

import DayOverview from './components/dayOverview/DayOverview';
import { SeasonDivider } from './components/gameTypeDivider/GameTypeDivider';

import {
    ButtonContainer,
    ButtonText,
    Legend,
    LegendContainer,
    ListViewContainer,
    OddsProviderContainer,
} from './styles';

const NoGamesForLeague: FC = () => (
    <NoContent
        primaryText={
            <FormattedMessage
                description="There are no games scheduled for this week."
                defaultMessage="There are no games scheduled for this week."
            />
        }
    />
);

const NoGamesForFranchise: FC = () => (
    <NoContent
        primaryText={
            <FormattedMessage
                description="There are no games that match this criteria."
                defaultMessage="There are no games that match this criteria."
            />
        }
    />
);

const SCROLL_THRESHOLD_FOR_SELECTION = 250;

const ListView: FC<ViewProps> = ({
    dateFromURL,
    handleNextOrPreviousDate,
    isLeaguePage,
    isFullSeason,
    onHowToWatchClick,
    selectedTeam,
    setSchedule,
    setScheduleDate,
    setScheduleSeason,
    setSelectedDate,
    setClubCalendarLink,
    timezone,
    resolvedRedirectDate,
}) => {
    const params = useParams();

    const [hasScrolled, setHasScrolled] = useState(false);
    const [hideBettingOdds, setHideBettingOdds] = useState<boolean>(false);

    const dayRefs = useRef<
        Array<{
            date: DateTime;
            element: HTMLElement;
        }>
    >([]);

    const targetDate = resolvedRedirectDate && dayRefs?.current.find((ref) => resolvedRedirectDate <= ref.date);

    useEffect(() => {
        if (targetDate && !hasScrolled && isFullSeason && resolvedRedirectDate) {
            targetDate.element.scrollIntoView({ block: 'start', behavior: 'smooth' });
            setScheduleDate(resolvedRedirectDate);
            setHasScrolled(true);
        }
    }, [hasScrolled, isFullSeason, isLeaguePage, resolvedRedirectDate, setScheduleDate, targetDate]);

    const onScrollChange = useCallback(() => {
        dayRefs.current.forEach((dayRef) => {
            const top = dayRef.element.getBoundingClientRect().top || Infinity;
            if (top <= SCROLL_THRESHOLD_FOR_SELECTION) {
                setSelectedDate(dayRef.date);
            }
        });
    }, [setSelectedDate]);

    useScroll({
        onScrollChanged: onScrollChange,
    });

    const isAllTeams = selectedTeam.value === 'all';

    const { data: countryCode = defaultLocationData } = useLocationQuery({});

    const {
        data: leagueWeekSchedule,
        isLoading: isLeagueWeekScheduleLoading,
        isInitialLoad: isLeagueWeekScheduleInitialLoad,
        resolvedDate: resolvedLeagueDate,
        refetch: refetchLeagueWeekSchedule,
    } = useScheduleQuery(dateFromURL, !isFullSeason && isLeaguePage);

    const {
        data: fullSeasonSchedule,
        isLoading: isFullSeasonLoading,
        isInitialLoad: isFullSeasonInitialLoad,
        resolvedSeason,
        refetch: refetchFullSeasonSchedule,
    } = useClubSeasonScheduleQuery(
        ShortCodeEnums.TeamTriCode[(selectedTeam.value as string).toUpperCase()],
        isFullSeason && !isAllTeams,
        dateFromURL ? `${dateFromURL}${Number(dateFromURL) + 1}` : undefined
    );

    const {
        data: clubWeekSchedule,
        isLoading: isClubWeekScheduleLoading,
        isInitialLoad: isClubWeekScheduleInitialLoad,
        resolvedDate: resolvedClubWeekDate,
        refetch: refetchClubWeekSchedule,
    } = useClubScheduleWeekQuery(
        ShortCodeEnums.TeamTriCode[(selectedTeam.value as string).toUpperCase()],
        !isFullSeason && !isLeaguePage,
        dateFromURL
    );

    let isLoading: boolean;
    let isInitialLoad: boolean;
    let scheduleDate: DateTime | undefined;
    let refetch: () => void;

    if (isFullSeason) {
        isLoading = isFullSeasonLoading;
        isInitialLoad = isFullSeasonInitialLoad;
        refetch = refetchFullSeasonSchedule;
    } else if (isLeaguePage) {
        isLoading = isLeagueWeekScheduleLoading;
        isInitialLoad = isLeagueWeekScheduleInitialLoad;
        scheduleDate = resolvedLeagueDate;
        refetch = refetchLeagueWeekSchedule;
    } else {
        isLoading = isClubWeekScheduleLoading;
        isInitialLoad = isClubWeekScheduleInitialLoad;
        scheduleDate = resolvedClubWeekDate;
        refetch = refetchClubWeekSchedule;
    }

    const schedule: ScheduleType = useMemo(() => {
        if (isFullSeason) {
            return getGamesByDay(fullSeasonSchedule);
        } else if (isAllTeams) {
            return leagueWeekSchedule;
        } else if (isLeaguePage) {
            return {
                ...leagueWeekSchedule,
                gameWeek: leagueWeekSchedule.gameWeek?.map((gameDay) => {
                    const filteredGames = gameDay.games.filter(
                        (game) =>
                            game.homeTeam.abbrev.toLowerCase() === selectedTeam.value ||
                            game.awayTeam.abbrev.toLowerCase() === selectedTeam.value
                    );

                    return {
                        ...gameDay,
                        games: filteredGames,
                        numberOfGames: filteredGames.length,
                    };
                }),
            };
        } else {
            return getGamesByDay(clubWeekSchedule);
        }
    }, [
        clubWeekSchedule,
        fullSeasonSchedule,
        isAllTeams,
        isFullSeason,
        isLeaguePage,
        leagueWeekSchedule,
        selectedTeam,
    ]);

    const games: Array<ScheduleGame> = useMemo(() => {
        const _games: Array<ScheduleGame> = [];

        schedule.gameWeek?.forEach((day) => {
            _games.push(...day.games);
        });

        return _games;
    }, [schedule]);

    const oddDetails: OddDetails = useMemo(() => {
        const { oddsPartners } = schedule;

        if (!oddsPartners) {
            return {
                daysWithOdds: {},
                oddsExist: false,
            };
        }

        const bettingOddPartnerIndex = oddsPartners.findIndex(
            (partner: BettingOddPartner) => partner.country === countryCode.toUpperCase()
        );

        const bettingOddPartner = oddsPartners[bettingOddPartnerIndex];

        const daysWithOdds =
            (bettingOddPartner &&
                games?.reduce<DaysWithOdds>((d, game) => {
                    if (
                        game.homeTeam.odds &&
                        game.homeTeam.odds.find((odd) => odd.providerId === bettingOddPartner?.partnerId)
                    ) {
                        const date = DateTime.fromISO(game.startTimeUTC).toISODate();

                        if (date !== null) {
                            d[date] = true;
                        }
                    }

                    return d;
                }, {})) ||
            {};

        return {
            bettingOddPartner,
            daysWithOdds,
            oddsExist: Object.keys(daysWithOdds)?.length > 0,
        };
    }, [schedule, games, countryCode]);

    const dayComponents: ReactNode = useMemo(() => {
        dayRefs.current = [];

        if (schedule.numberOfGames === 0) {
            return isLeaguePage ? <NoGamesForLeague /> : <NoGamesForFranchise />;
        }

        return schedule.gameWeek?.map((day) => (
            <Fragment key={day.date}>
                {day.date === schedule?.regularSeasonStartDate && (
                    <SeasonDivider
                        isStartOfSeason
                        isStartOfGameType={resolvedLeagueDate?.toISODate() === schedule.regularSeasonStartDate}
                    />
                )}
                <DayOverview
                    day={day}
                    ref={(element) => element && dayRefs.current.push({ date: DateTime.fromISO(day.date), element })}
                    oddDetails={oddDetails}
                    hideBettingOdds={hideBettingOdds}
                    pageName={isLeaguePage ? PageName.Schedule : PageName.ClubSchedule}
                    timezone={timezone}
                    teamTriCode={!isLeaguePage ? (selectedTeam.value as string) : undefined}
                    isFullSeason={isFullSeason}
                    onHowToWatchClick={onHowToWatchClick}
                />
                {day.date === schedule?.regularSeasonEndDate && (
                    <SeasonDivider
                        isStartOfSeason={false}
                        isStartOfGameType={resolvedLeagueDate?.toISODate() === schedule.regularSeasonEndDate}
                    />
                )}
            </Fragment>
        ));
    }, [hideBettingOdds, isFullSeason, isLeaguePage, oddDetails, resolvedLeagueDate, schedule, selectedTeam, timezone]);

    useGamePolling(refetch, games);

    useEffect(() => {
        setScheduleDate(scheduleDate);
    }, [setScheduleDate, scheduleDate]);

    useEffect(() => {
        if (resolvedSeason && fullSeasonSchedule.games && fullSeasonSchedule.games.length > 0) {
            setScheduleSeason(resolvedSeason);
            setScheduleDate(DateTime.fromISO(fullSeasonSchedule.games[0].gameDate));
        }
    }, [fullSeasonSchedule, resolvedSeason, setScheduleSeason, setScheduleDate]);

    useEffect(() => {
        setSchedule(schedule);
    }, [setSchedule, schedule]);

    useEffect(() => {
        if (!isLeaguePage && clubWeekSchedule.calendarUrl) {
            setClubCalendarLink(clubWeekSchedule.calendarUrl);
        }
    }, [clubWeekSchedule.calendarUrl, isLeaguePage, schedule, setClubCalendarLink]);

    const checkValidDate = (date: string | undefined): boolean => date !== '' && date !== undefined;

    return (
        <ListViewContainer
            id={`${params.view}-tabpanel`}
            role="tabpanel"
            aria-labelledby={params.view}
            $isClubPage={!isLeaguePage}
        >
            {isInitialLoad ? (
                <LoadingDisplay isLoading />
            ) : (
                <LoadingDisplay isLoading={isLoading}>
                    {oddDetails.oddsExist && oddDetails.bettingOddPartner && (
                        <OddsProviderContainer>
                            <OddsProvider
                                bettingOddPartner={oddDetails.bettingOddPartner}
                                showToggle
                                hideBettingOdds={hideBettingOdds}
                                toggleBettingOdds={() => setHideBettingOdds(!hideBettingOdds)}
                            />
                        </OddsProviderContainer>
                    )}

                    {dayComponents || null}

                    {schedule.numberOfGames > 0 && (
                        <LegendContainer>
                            <Legend aria-hidden>
                                <EventIndicatorSmall $eventType={SpecialEvent.preseason} />
                                <FormattedMessage
                                    {...commonMessages.gameTypeDescriptions.long[IdEnums.GameType.preseason]}
                                />
                            </Legend>
                            <Legend aria-hidden>
                                <EventIndicatorSmall $eventType={SpecialEvent.playoffs} />
                                <FormattedMessage
                                    {...commonMessages.gameTypeDescriptions.long[IdEnums.GameType.playoffs]}
                                />
                            </Legend>
                            <Legend aria-hidden>
                                <EventIndicatorSmall $eventType={SpecialEvent.event} />
                                <FormattedMessage {...commonMessages.gameTypeDescriptions.long.nhlEvent} />
                            </Legend>
                        </LegendContainer>
                    )}

                    {params.view !== ScheduleViewType.SEASON && (
                        <ButtonContainer>
                            {checkValidDate(schedule.previousStartDate) && (
                                <ClubScheduleButton
                                    variant={ButtonType.Secondary}
                                    onClick={(e) =>
                                        handleNextOrPreviousDate(
                                            DateTime.fromISO(schedule.previousStartDate),
                                            e.target as HTMLButtonElement
                                        )
                                    }
                                >
                                    <ChevronLeftIcon />
                                    <ButtonText>
                                        <FormattedMessage
                                            description="A button that allows you to go the previous set of games"
                                            defaultMessage="Previous"
                                        />
                                    </ButtonText>
                                </ClubScheduleButton>
                            )}
                            {checkValidDate(schedule.nextStartDate) && (
                                <ClubScheduleButton
                                    variant={ButtonType.Secondary}
                                    onClick={(e) =>
                                        handleNextOrPreviousDate(
                                            DateTime.fromISO(schedule.nextStartDate),
                                            e.target as HTMLButtonElement
                                        )
                                    }
                                >
                                    <ButtonText $isLeft>
                                        <FormattedMessage
                                            description="A button that allows you to go the next set of games"
                                            defaultMessage="Next"
                                        />
                                    </ButtonText>
                                    <ChevronRightIcon />
                                </ClubScheduleButton>
                            )}
                        </ButtonContainer>
                    )}
                </LoadingDisplay>
            )}
        </ListViewContainer>
    );
};

export default ListView;
